// Trilingual display for lab report PANEL/SECTION headers (e.g. "Гематология" /
// "Hematology" / "המטולוגיה-כללי") — the group heading above a cluster of
// analyte cards on the "Показатели" page, not the analyte name itself.
//
// This is a small, static, hand-maintained dictionary rather than an AI call:
// unlike analyte names (hundreds of variants), report sections come from a
// short, near-fixed vocabulary across the Russian and Israeli lab formats
// this app has actually seen — free, instant, works offline, and never
// invents a translation for something it doesn't recognize (docs/AI_RULES.md
// rule 1). The database keeps whatever was printed on the report
// (`LabResult.panel`) completely unchanged; this module only affects display.

export interface PanelTranslation {
  ru: string;
  en: string;
  he: string;
}

interface PanelEntry extends PanelTranslation {
  // Extra spellings that resolve to this entry but are never displayed —
  // a report may head the same section "Spermogram" or "Semen analysis".
  aliases?: string[];
}

// Only sections actually seen on real reports processed by this app so far.
// Add more entries here as new report formats surface unrecognized panels —
// an unrecognized panel simply falls back to showing the raw printed text
// (see translatePanel below), never a guessed translation.
const PANEL_DICTIONARY: PanelEntry[] = [
  { ru: 'Эндокринология', en: 'Endocrinology', he: 'אנדוקרינולוגיה' },
  { ru: 'Бактериология', en: 'Bacteriology', he: 'בקטריולוגיה' },
  { ru: 'Гематология', en: 'Hematology', he: 'המטולוגיה-כללי' },
  { ru: 'Химия крови', en: 'Blood chemistry', he: 'כימיה בדם' },
  { ru: 'Общий анализ мочи', en: 'Urinalysis', he: 'שתן-כללי' },
  { ru: 'Спермограмма', en: 'Spermogram', he: 'בדיקת זרע', aliases: ['semen analysis', 'sperm analysis', 'спермограмма'] },
];

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

const LOOKUP = new Map<string, PanelEntry>();
for (const entry of PANEL_DICTIONARY) {
  LOOKUP.set(normalize(entry.ru), entry);
  LOOKUP.set(normalize(entry.en), entry);
  LOOKUP.set(normalize(entry.he), entry);
  for (const alias of entry.aliases || []) LOOKUP.set(normalize(alias), entry);
}

export type PanelLang = 'ru' | 'en' | 'he';

// Hebrew and Cyrillic are detected by Unicode block; anything else (Latin,
// digits, punctuation) is treated as English. Good enough for panel headers,
// which are short single-script labels in practice.
function detectLang(raw: string): PanelLang {
  if (/[\u0590-\u05FF]/.test(raw)) return 'he';
  if (/[\u0400-\u04FF]/.test(raw)) return 'ru';
  return 'en';
}

export interface PanelHeading extends PanelTranslation {
  originalLang: PanelLang;
}

// Returns null when the panel text isn't in the dictionary — callers should
// fall back to showing `raw` alone rather than inventing a translation.
// When found, the segment matching the original's detected script is the
// verbatim `raw` text (not the dictionary's canonical spelling), so the
// exact printed text is still what's shown and underlined as the source —
// only the other two languages come from the dictionary.
export function translatePanel(raw: string): PanelHeading | null {
  const entry = LOOKUP.get(normalize(raw));
  if (!entry) return null;
  const originalLang = detectLang(raw);
  return {
    ru: originalLang === 'ru' ? raw : entry.ru,
    en: originalLang === 'en' ? raw : entry.en,
    he: originalLang === 'he' ? raw : entry.he,
    originalLang,
  };
}
